import { z } from "zod";

import type { InferenceModelType } from "./models.model";

// e.g. for https://huggingface.co/meta-llama/Llama-3.2-11B-Vision-Instruct the identifier is the entire string after .co/ in the URL
export enum HuggingFaceIdentifier {
  META_LLAMA_3_1_70B_INSTRUCT = "meta-llama/Llama-3.1-70B-Instruct",
  META_LLAMA_3_3_70B_INSTRUCT = "meta-llama/Llama-3.3-70B-Instruct",
  META_LLAMA_3_1_8B_INSTRUCT = "meta-llama/Llama-3.1-8B-Instruct",
  META_LLAMA_3_2_11B_VISION_INSTRUCT = "meta-llama/Llama-3.2-11B-Vision-Instruct",
  META_LLAMA_3_2_1B_INSTRUCT = "meta-llama/Llama-3.2-1B-Instruct",
  META_LLAMA_3_2_3B_INSTRUCT = "meta-llama/Llama-3.2-3B-Instruct",
  MISTRAL_NEMO_12B_INSTRUCT = "nvidia/Mistral-NeMo-12B-Instruct",
  DEEPSEEK_V3_FP_8 = "deepseek-ai/DeepSeek-V3",
  DEEPSEEK_V3_0324_FP_8 = "deepseek-ai/DeepSeek-V3-0324",
  DEEPSEEK_V4_FLASH = "deepseek-ai/DeepSeek-V4-Flash",
  DEEPSEEK_R1_FP_8 = "deepseek-ai/DeepSeek-R1",
  DEEPSEEK_R1_0528_FP_8 = "deepseek-ai/DeepSeek-R1-0528",
  DEEPSEEK_VL2_TINY_FP16 = "deepseek-ai/deepseek-vl2-tiny",
  DEEPSEEK_VL2_SMALL_FP16 = "deepseek-ai/deepseek-vl2-small",
  QWEN_2_5_72B_INSTRUCT_FP_8 = "Qwen/Qwen2.5-72B-Instruct",
  HERMES_3_LLAMA_3_1_405B_FP_8 = "NousResearch/Hermes-3-Llama-3.1-405B",
  MYTHOMAX_L2_13B_BF_16 = "gryphe/Mythomax-L2-13B",
  UNSLOPNEMO_12B_V4_1 = "thedrummer/UnslopNemo-12B-v4.1",
  ROCINANTE_12B_V1_1 = "thedrummer/Rocinante-12B-v1.1",
  R1_DISTILL_LLAMA_70B_FP_8 = "deepseek-ai/DeepSeek-R1-Distill-Llama-70B",
  GEMMA_3_27B_INSTRUCT = "google/gemma-3-27b-it",
  NVIDIA_NEMOTRON_3_SUPER_120B = "nvidia/NVIDIA-Nemotron-3-Super-120B-A12B-BF16",
  QWEN_2_5_7B_INSTRUCT = "Qwen/Qwen2.5-7B-Instruct",
  QWEN_QWQ_32B_FP_8 = "Qwen/QWQ-32B",
  QWEN_3_30B_A3B_FP8 = "Qwen/Qwen3-30B-A3B-FP8",
  TENACITY_LABS_MAGNETA_CHAT_V3 = "Qwen/Qwen3-32B",
  OSMOSIS_STRUCTURE_0_6B_FP_32 = "osmosis-ai/Osmosis-Structure-0.6B",
  QWEN_3_EMBEDDING_4B = "Qwen/Qwen3-Embedding-4B",
  CLIP_TAGGER_12B = "inference-net/cliptagger-12b",
  CAL_AI_FOOD_VISION_27B = "context-labs/Cal-Distilled-Model",
  CAL_AI_FOOD_VISION_12B = "context-labs/Cal_Model_12B",
  CAL_AI_FOOD_VISION_V3 = "inference-net/cal-glm-4.5v",
  OPENAI_GPT_OSS_120B = "openai/gpt-oss-120b",
  OPENAI_GPT_OSS_20B = "openai/gpt-oss-20b",
  INFERENCE_NET_SCHEMATRON_3B = "inference-net/schematron-3b",
  INFERENCE_NET_SCHEMATRON_8B = "inference-net/schematron-8b",
  INFERENCE_NET_SCHEMATRON_V2_SMALL = "inference-net/schematron-v2-llama-3.2-3b",
  INFERENCE_NET_SCHEMATRON_V2_TURBO = "inference-net/schematron-v2-granite-4.0-h-micro",
  INFERENCE_NET_BDC_CODER_QWEN3_14B = "inference-net/bdc-coder-qwen3-14b",
  INFERENCE_NET_BDC_CODER = "context-labs/nubank-coder-v6",

  PROFOUND_ENTITY_RECOGNITION = "context-labs/profound-name-extractor-v1",

  TENACITY_LABS_MAGNETA_CHAT = "inference-net/tenacity-model",

  GRAVITY_XS = "inference-net/gravity-ads-llama-3.2-1b",
  GRAVITY_S = "inference-net/gravity-ads",

  RIZZ_CHAT = "inference-net/rizz-model",

  OLIVE_ANALYZE_PRODUCT_V1 = "inference-net/olive-qwen3.6-9b-half-lr",
}

/**
 * NOTE: Model Families should not contain any "-" dash characters because
 * this will break the url slug logic we have in modelFamilySlugToModelFamily.
 */
export const MODEL_FAMILIES = [
  "Llama 3.1 8B",
  "Llama 3.1 70B",
  "Llama 3.1 8B Instruct",
  "Llama 3.1 70B Instruct",
  "Llama 3.2 1B Instruct",
  "Llama 3.2 3B Instruct",
  "Llama 3.2 11B Vision Instruct",
  "Llama 3.3 70B Instruct",
  "Mistral Nemo 12B Instruct",
  "Hermes 3 Llama 3.1 405B",
  "Qwen 2.5 72B Instruct",
  "Qwen 2.5 7B Vision Instruct",
  "Mythomax",
  "UnslopNemo 12b v4.1",
  "Rocinante 12b v1.1",
  "DeepSeek R1",
  "DeepSeek R1 0528",
  "DeepSeek V3",
  "DeepSeek V3 0324",
  "DeepSeek V4 Flash",
  "DeepSeek R1 Distill Llama 70B",
  "DeepSeek VL2 Small",
  "DeepSeek VL2 Tiny",
  "Google Gemma 3",
  "Qwen QWQ 32B",
  "Qwen 3 30B A3B",
  "Magneta Chat V3",
  "Osmosis Structure 0.6B",
  "Qwen 3 Embedding 4B",
  "ClipTagger 12B",
  "CalAI Food Vision 27B",
  "CalAI Food Vision 12B",
  "CalAI Food Vision V3",
  "OpenAI gpt-oss 120B",
  "OpenAI gpt-oss 20B",
  "Schematron 3B",
  "Schematron 8B",
  "Schematron V2 Small",
  "Schematron V2 Turbo",
  "BDC Coder 14b",
  "BDC Coder",
  "Profound Entity Recognition",
  "Magneta Chat",
  "Nemotron 3 Super",
  "Olive Analyze Product V1",
] as const;

export const ModelFamilySchema = z.enum(MODEL_FAMILIES);

export type ModelFamily = (typeof MODEL_FAMILIES)[number];

export const MODEL_FAMILY_SET: ReadonlySet<ModelFamily> = new Set<ModelFamily>(
  MODEL_FAMILIES,
);

export function isKnownModelFamily(value: string): value is ModelFamily {
  return MODEL_FAMILY_SET.has(value as ModelFamily);
}

export const MODEL_MAKERS = [
  "Anthropic",
  "Cerebras",
  "Meta",
  "Mistral",
  "RLAIF",
  "DeepSeek",
  "Groq",
  "Qwen",
  "Nous",
  "TheDrummer",
  "Gryphe",
  "Google",
  "Osmosis",
  "GrassData",
  "CalAI",
  "OpenAI",
  "InferenceNet",
  "Profound",
  "Tenacity Labs",
  "NVIDIA",
  "Olive",
  "xAI",
  "Moonshot AI",
  "Z.ai",
  "MiniMax",
] as const;

export const ModelMakerSchema = z.enum(MODEL_MAKERS);

export type ModelMaker = (typeof MODEL_MAKERS)[number];

export const KNOWN_MODEL_MAKER_BY_CREATOR: ReadonlyMap<string, ModelMaker> =
  new Map<string, ModelMaker>([
    ["anthropic", "Anthropic"],
    ["cerebras", "Cerebras"],
    ["deepseek", "DeepSeek"],
    ["google", "Google"],
    ["gemini", "Google"],
    ["groq", "Groq"],
    ["grassdata", "GrassData"],
    ["inference", "InferenceNet"],
    ["inference-net", "InferenceNet"],
    ["meta", "Meta"],
    ["meta-llama", "Meta"],
    ["minimax", "MiniMax"],
    ["minimaxai", "MiniMax"],
    ["mistral", "Mistral"],
    ["moonshot", "Moonshot AI"],
    ["moonshotai", "Moonshot AI"],
    ["nvidia", "NVIDIA"],
    ["openai", "OpenAI"],
    ["qwen", "Qwen"],
    ["x-ai", "xAI"],
    ["xai", "xAI"],
    ["z-ai", "Z.ai"],
    ["zai", "Z.ai"],
    ["zhipu", "Z.ai"],
  ]);

export function parseKnownModelMaker(value: string): ModelMaker | null {
  const parsed = ModelMakerSchema.safeParse(value);
  if (parsed.success) {
    return parsed.data;
  }
  return KNOWN_MODEL_MAKER_BY_CREATOR.get(value.trim().toLowerCase()) ?? null;
}

export const MODEL_TYPES = [
  "Text to Text",
  "Image to Text",
  "Embeddings",
] as const;

export type ModelType = (typeof MODEL_TYPES)[number];

export const InferenceModelMetadataSchema = z.object({
  // TODO: this is named "family" but seems to be rendering as a title of the actual model on the website, and should probably be renamed to "model_title"
  family: ModelFamilySchema,
  type: z.enum(MODEL_TYPES),
  quantization: z.string(),
  maker: ModelMakerSchema,
  response_format_json_object: z.boolean(),
  tools: z.boolean(),
  image_input: z.boolean(),
  structured_outputs: z.boolean(),
  context_size: z.number(),
  max_output_tokens: z.number(),
  is_model_supported: z.boolean(),
  is_model_public: z.boolean(),
  is_shown_on_playground: z.boolean().optional(),
  hugging_face_identifier: z.nativeEnum(HuggingFaceIdentifier).optional(),
  model_name: z.string(),
  supports_reasoning: z.boolean().nullish().default(false),
  parameters: z.string(),
  offline: z.boolean().default(false), // if true, openrouter will not route to this model
});

export type InferenceModelMetadata = z.infer<
  typeof InferenceModelMetadataSchema
>;

export const INFERENCE_MODEL_METADATA: Partial<
  Record<string, InferenceModelMetadata>
> = {
  "meta-llama/llama-3.1-8b/fp-8": {
    family: "Llama 3.1 8B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.1 8B FP8",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: true,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_8B_INSTRUCT,
    supports_reasoning: false,
    parameters: "8B",
    offline: true,
  },
  "meta-llama/llama-3.1-70b/fp-8": {
    family: "Llama 3.1 70B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.1 70B FP8",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_70B_INSTRUCT,
    supports_reasoning: false,
    parameters: "70B",
    offline: true,
  },
  // Because we use this model as the default for testing, we need to enable JSON mode and structured outputs.
  "meta-llama/llama-3.1-8b-instruct/q4-k-m": {
    family: "Llama 3.1 8B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "q4-k-m",
    model_name: "Meta Llama 3.1 8B Instruct Q4-K-M",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_8B_INSTRUCT,
    supports_reasoning: false,
    parameters: "8B",
    offline: true,
  },
  "meta-llama/llama-3.1-8b-instruct/fp-8": {
    family: "Llama 3.1 8B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.1 8B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_8B_INSTRUCT,
    supports_reasoning: false,
    parameters: "8B",
    offline: true,
  },
  "meta-llama/llama-3.1-8b-instruct/fp-16": {
    family: "Llama 3.1 8B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.1 8B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_8B_INSTRUCT,
    supports_reasoning: false,
    parameters: "8B",
    offline: true,
  },
  "meta-llama/llama-3.1-70b-instruct/fp-8": {
    family: "Llama 3.1 70B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.1 70B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,

    // TODO: enable this model
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_70B_INSTRUCT,
    supports_reasoning: false,
    parameters: "70B",
    offline: true,
  },
  "meta-llama/llama-3.1-70b-instruct/fp-16": {
    family: "Llama 3.1 70B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.1 70B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_1_70B_INSTRUCT,
    supports_reasoning: false,
    parameters: "70B",
    offline: true,
  },
  "meta-llama/llama-3.3-70b-instruct/fp-8": {
    family: "Llama 3.3 70B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.3 70B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 128_000,
    max_output_tokens: 16_384,
    // TODO: enable this model
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_3_70B_INSTRUCT,
    supports_reasoning: false,
    parameters: "70B",
    offline: true,
  },
  "meta-llama/llama-3.3-70b-instruct/fp-16": {
    family: "Llama 3.3 70B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.3 70B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 128_000,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_3_70B_INSTRUCT,
    supports_reasoning: false,
    parameters: "70B",
    offline: true,
  },
  // Note: for now, we need to use vLLM to serve llama 3.2 1b/3b models in order to get JSON mode to work
  "meta-llama/llama-3.2-1b-instruct/fp-8": {
    family: "Llama 3.2 1B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.2 1B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_2_1B_INSTRUCT,
    supports_reasoning: false,
    parameters: "1B",
    offline: true,
  },
  // Note: for now, we need to use vLLM to serve llama 3.2 1b/3b models in order to get JSON mode to work
  "meta-llama/llama-3.2-1b-instruct/fp-16": {
    family: "Llama 3.2 1B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.2 1B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_2_1B_INSTRUCT,
    supports_reasoning: false,
    parameters: "1B",
    offline: true,
  },
  // Note: for now, we need to use vLLM to serve llama 3.2 1b/3b models in order to get JSON mode to work
  "meta-llama/llama-3.2-3b-instruct/fp-8": {
    family: "Llama 3.2 3B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.2 3B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_2_3B_INSTRUCT,
    supports_reasoning: false,
    parameters: "3B",
    offline: true,
  },
  // Note: for now, we need to use vLLM to serve llama 3.2 1b/3b models in order to get JSON mode to work
  "meta-llama/llama-3.2-3b-instruct/fp-16": {
    family: "Llama 3.2 3B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.2 3B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_2_3B_INSTRUCT,
    supports_reasoning: false,
    parameters: "3B",
    offline: true,
  },
  "meta-llama/llama-3.2-11b-instruct/fp-16": {
    family: "Llama 3.2 11B Vision Instruct",
    // TODO: this can also do image to text, how should we represent this?
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.2 11B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier:
      HuggingFaceIdentifier.META_LLAMA_3_2_11B_VISION_INSTRUCT,
    supports_reasoning: false,
    parameters: "11B",
    offline: true,
  },
  "mistralai/mistral-nemo-12b-instruct/fp-8": {
    family: "Mistral Nemo 12B Instruct",
    type: "Text to Text",
    maker: "Mistral",
    quantization: "fp-8",
    model_name: "Mistral Nemo 12B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    // it works well with vllm, but not sglang (yet)
    tools: true,
    context_size: 16_384,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.MISTRAL_NEMO_12B_INSTRUCT,
    supports_reasoning: false,
    parameters: "12B",
    offline: true,
  },
  "RLAIF/sft-llama-3.1-8b-external/fp-16": {
    family: "Llama 3.1 8B Instruct",
    type: "Text to Text",
    maker: "RLAIF",
    quantization: "fp-16",
    model_name: "RLAIF SFT Llama 3.1 8B External FP16",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 4_096,
    max_output_tokens: 4_096,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: undefined,
    supports_reasoning: false,
    parameters: "8B",
    offline: true,
  },
  "deepseek/deepseek-v3/fp-8": {
    family: "DeepSeek V3",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-8",
    model_name: "DeepSeek V3 FP8",
    response_format_json_object: false,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_V3_FP_8,
    supports_reasoning: false,
    parameters: "685B",
    offline: true,
  },

  "nous/hermes-3-llama-3.1-405b/fp-8": {
    family: "Hermes 3 Llama 3.1 405B",
    type: "Text to Text",
    maker: "Nous",
    quantization: "fp-8",
    model_name: "Hermes 3 Llama 3.1 405B FP8",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 32_768,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.HERMES_3_LLAMA_3_1_405B_FP_8,
    supports_reasoning: false,
    parameters: "405B",
    offline: false,
  },
  "qwen/qwen2.5-72b-instruct/fp-8": {
    family: "Qwen 2.5 72B Instruct",
    type: "Text to Text",
    maker: "Qwen",
    quantization: "fp-8",
    model_name: "Qwen 2.5 72B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.QWEN_2_5_72B_INSTRUCT_FP_8,
    supports_reasoning: false,
    parameters: "72B",
    offline: true,
  },
  "deepseek/deepseek-r1/fp-8": {
    family: "DeepSeek R1",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-8",
    model_name: "DeepSeek R1 FP8",
    response_format_json_object: false,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false, // keep true over the weekend for team testing
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_R1_FP_8,
    supports_reasoning: true,
    parameters: "671B",
    offline: true,
  },
  "deepseek/deepseek-r1-0528/fp-8": {
    family: "DeepSeek R1 0528",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-8",
    model_name: "DeepSeek R1 0528 FP8",
    response_format_json_object: false,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false, // keep true over the weekend for team testing
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_R1_FP_8,
    supports_reasoning: true,
    parameters: "671B",
    offline: true,
  },
  "thedrummer/unslopnemo-12b-v4.1/bf-16": {
    family: "UnslopNemo 12b v4.1",
    type: "Text to Text",
    maker: "TheDrummer",
    quantization: "bf-16",
    model_name: "UnslopNemo 12B v4.1 BF16",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    // TODO: enable this model
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.UNSLOPNEMO_12B_V4_1,
    supports_reasoning: false,
    parameters: "12B",
    offline: false,
  },
  "thedrummer/rocinante-12b-v1.1/bf-16": {
    family: "Rocinante 12b v1.1",
    type: "Text to Text",
    maker: "TheDrummer",
    quantization: "bf-16",
    model_name: "Rocinante 12B v1.1 BF16",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    // TODO: enable this model
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.ROCINANTE_12B_V1_1,
    supports_reasoning: false,
    parameters: "12B",
    offline: false,
  },
  "gryphe/mythomax-l2-13b/bf-16": {
    family: "Mythomax",
    type: "Text to Text",
    maker: "Gryphe",
    quantization: "bf-16",
    model_name: "Mythomax L2 13B BF16",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 4_096,
    max_output_tokens: 4_096,
    // TODO: enable this model
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.MYTHOMAX_L2_13B_BF_16,
    supports_reasoning: false,
    parameters: "13B",
    offline: false,
  },
  "deepseek/r1-distill-llama-70b/fp-8": {
    family: "DeepSeek R1 Distill Llama 70B",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-8",
    model_name: "DeepSeek R1 Distill Llama 70B FP8",
    response_format_json_object: false,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.R1_DISTILL_LLAMA_70B_FP_8,
    supports_reasoning: true,
    parameters: "70B",
    offline: true,
  },
  "google/gemma-3-27b-instruct/bf-16": {
    family: "Google Gemma 3",
    type: "Text to Text",
    maker: "Google",
    quantization: "bf-16",
    model_name: "Google Gemma 3 27B Instruct",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 8_192,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.GEMMA_3_27B_INSTRUCT,
    supports_reasoning: false,
    parameters: "27B",
    offline: true,
  },

  "qwen/qwen2.5-7b-instruct/bf-16": {
    family: "Qwen 2.5 7B Vision Instruct",
    type: "Text to Text",
    maker: "Qwen",
    quantization: "bf-16",
    model_name: "Qwen 2.5 7B Vision Instruct BF16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 8_192,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.QWEN_2_5_7B_INSTRUCT,
    supports_reasoning: false,
    parameters: "7B",
    offline: true,
  },

  "qwen/qwen3-30b-a3b/fp8": {
    family: "Qwen 3 30B A3B",
    type: "Text to Text",
    maker: "Qwen",
    quantization: "fp8",
    model_name: "Qwen 3 30B A3B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.QWEN_3_30B_A3B_FP8,
    supports_reasoning: false,
    parameters: "30B",
    offline: true,
  },
  "tenacity-labs/magneta-chat-v3": {
    family: "Magneta Chat V3",
    type: "Text to Text",
    maker: "Tenacity Labs",
    quantization: "bf-16",
    model_name: "Magneta Chat V3",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 32_768,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier:
      HuggingFaceIdentifier.TENACITY_LABS_MAGNETA_CHAT_V3,
    supports_reasoning: true,
    parameters: "32B",
    offline: false,
  },

  "deepseek/deepseek-v3-0324/fp-8": {
    family: "DeepSeek V3 0324",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-8",
    model_name: "DeepSeek V3 0324 FP8",
    response_format_json_object: false,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_V3_0324_FP_8,
    supports_reasoning: false,
    parameters: "685B",
    offline: true,
  },

  // Private model: served on vllm but not publicly listed. Access is gated to
  // the system account and internal inference.net teams via
  // PRIVATE_MODELS_APPROVED_SET in @kuzco/utils ModelPricing.
  "deepseek/deepseek-v4-flash": {
    family: "DeepSeek V4 Flash",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-8",
    model_name: "DeepSeek V4 Flash",
    response_format_json_object: false,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 32_768,
    // Supported so the static catalog serving path resolves and the
    // PRIVATE_MODELS_APPROVED_SET allowlist is enforced; not public so it
    // stays unlisted.
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_V4_FLASH,
    supports_reasoning: false,
    parameters: "685B",
    offline: true,
  },

  "qwen/qwq-32b/fp-8": {
    family: "Qwen QWQ 32B",
    type: "Text to Text",
    maker: "Qwen",
    quantization: "fp-8",
    model_name: "Qwen QWQ 32B FP8",
    response_format_json_object: false,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 16_384,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.QWEN_QWQ_32B_FP_8,
    supports_reasoning: true,
    parameters: "32B",
    offline: true,
  },
  "deepseek/deepseek-vl2-tiny/fp-16": {
    family: "DeepSeek VL2 Tiny",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-16",
    model_name: "DeepSeek VL2 Tiny FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 4096,
    max_output_tokens: 4096,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_VL2_TINY_FP16,
    supports_reasoning: false,
    parameters: "3B",
    offline: true,
  },

  "deepseek/deepseek-vl2-small/fp-16": {
    family: "DeepSeek VL2 Small",
    type: "Text to Text",
    maker: "DeepSeek",
    quantization: "fp-16",
    model_name: "DeepSeek VL2 Small FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 4096,
    max_output_tokens: 4096,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.DEEPSEEK_VL2_SMALL_FP16,
    supports_reasoning: false,
    parameters: "16B",
    offline: true,
  },

  "osmosis-ai/osmosis-structure-0.6b/fp-32": {
    family: "Osmosis Structure 0.6B",
    type: "Text to Text",
    maker: "Osmosis",
    quantization: "fp-32",
    model_name: "Osmosis Structure 0.6B FP32",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 4096,
    max_output_tokens: 4096,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.OSMOSIS_STRUCTURE_0_6B_FP_32,
    supports_reasoning: false,
    parameters: "0.6B",
    offline: true,
  },

  "qwen/qwen3-embedding-4b": {
    family: "Qwen 3 Embedding 4B",
    type: "Embeddings",
    maker: "Qwen",
    quantization: "fp-16",
    model_name: "Qwen 3 Embedding 4B",
    response_format_json_object: false,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 32_768,
    max_output_tokens: 0, // Embeddings don't produce text output
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.QWEN_3_EMBEDDING_4B,
    supports_reasoning: false,
    parameters: "4B",
    offline: true,
  },

  "inference-net/cliptagger-12b": {
    family: "ClipTagger 12B",
    type: "Image to Text",
    maker: "GrassData",
    quantization: "fp-8",
    model_name: "ClipTagger 12B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 8_192,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.CLIP_TAGGER_12B,
    supports_reasoning: false,
    parameters: "12B",
    offline: true,
  },
  "wynd/wynd-vidcap-12b": {
    family: "ClipTagger 12B",
    type: "Image to Text",
    maker: "GrassData",
    quantization: "fp-8",
    model_name: "ClipTagger 12B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 8_192,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.CLIP_TAGGER_12B,
    supports_reasoning: false,
    parameters: "12B",
    offline: true,
  },

  "cal-ai/food-vision-27b": {
    family: "CalAI Food Vision 27B",
    type: "Text to Text",
    maker: "CalAI",
    quantization: "fp-8",
    model_name: "CalAI Food Vision 27B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 8_192,
    max_output_tokens: 8_192,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.CAL_AI_FOOD_VISION_27B,
    supports_reasoning: false,
    parameters: "27B",
    offline: false,
  },

  "cal-ai/food-vision-12b": {
    family: "CalAI Food Vision 12B",
    type: "Image to Text",
    maker: "CalAI",
    quantization: "fp-8",
    model_name: "CalAI Food Vision 12B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 8_192,
    max_output_tokens: 8_192,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.CAL_AI_FOOD_VISION_12B,
    supports_reasoning: false,
    parameters: "12B",
    offline: true,
  },

  "cal-ai/food-vision-v3": {
    family: "CalAI Food Vision V3",
    type: "Image to Text",
    maker: "CalAI",
    quantization: "bf-16",
    model_name: "CalAI Food Vision V3",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 2500,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.CAL_AI_FOOD_VISION_V3,
    supports_reasoning: false,
    parameters: "100B",
    offline: false,
  },

  "openai/gpt-oss-120b": {
    family: "OpenAI gpt-oss 120B",
    type: "Text to Text",
    maker: "OpenAI",
    quantization: "mxfp4",
    model_name: "OpenAI gpt-oss 120B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 100_000,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.OPENAI_GPT_OSS_120B,
    supports_reasoning: true,
    parameters: "120B",
    offline: true,
  },

  "openai/gpt-oss-20b": {
    family: "OpenAI gpt-oss 20B",
    type: "Text to Text",
    maker: "OpenAI",
    quantization: "mxfp4",
    model_name: "OpenAI gpt-oss 20B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 100_000,
    max_output_tokens: 32_768,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.OPENAI_GPT_OSS_20B,
    supports_reasoning: true,
    parameters: "20B",
    offline: true,
  },

  // Retired; inbound traffic is rewritten to schematron-v2-turbo by
  // GenerationService.validateGenerationRequest. `is_model_supported` is
  // intentionally left `true` so a one-commit revert of the rewrite would
  // fall back to serving v1 directly from the (still-running) GPU schedule
  // rather than turning into a hard catalog rejection. `is_model_public:
  // false` is what hides the entry from public lists and the playground.
  "inference-net/schematron-3b": {
    family: "Schematron 3B",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "Schematron 3B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.INFERENCE_NET_SCHEMATRON_3B,
    supports_reasoning: false,
    parameters: "3B",
    offline: false,
  },

  // Retired; inbound traffic is rewritten to schematron-v2-small by
  // GenerationService.validateGenerationRequest. `is_model_supported` is
  // intentionally left `true` so a one-commit revert of the rewrite would
  // fall back to serving v1 directly from the (still-running) GPU schedule
  // rather than turning into a hard catalog rejection. `is_model_public:
  // false` is what hides the entry from public lists and the playground.
  "inference-net/schematron-8b": {
    family: "Schematron 8B",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "Schematron 8B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.INFERENCE_NET_SCHEMATRON_8B,
    supports_reasoning: false,
    parameters: "8B",
    offline: false,
  },

  "inference-net/bdc-coder-qwen3-14b": {
    family: "BDC Coder 14b",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "BDC Coder 14b",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 32_768,
    max_output_tokens: 4_096,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier:
      HuggingFaceIdentifier.INFERENCE_NET_BDC_CODER_QWEN3_14B,
    supports_reasoning: false,
    parameters: "14B",
    offline: false,
  },

  "inference-net/bdc-coder": {
    family: "BDC Coder",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "BDC Coder",
    response_format_json_object: true,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 28_000,
    max_output_tokens: 10_000,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.INFERENCE_NET_BDC_CODER,
    supports_reasoning: false,
    parameters: "32B",
    offline: false,
  },

  "profound-ai/profound-entity-recognition": {
    family: "Profound Entity Recognition",
    type: "Text to Text",
    maker: "Profound",
    quantization: "bf-16",
    model_name: "Profound Entity Recognition",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 32_000,
    max_output_tokens: 10_000,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.PROFOUND_ENTITY_RECOGNITION,
    supports_reasoning: false,
    parameters: "8B",
    offline: false,
  },

  "inference-net/load-test": {
    family: "Llama 3.2 1B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Llama 3.2 1B Instruct FP16",
    response_format_json_object: false,
    structured_outputs: true,
    image_input: true,
    tools: true,
    context_size: 32_000,
    max_output_tokens: 10_000,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.META_LLAMA_3_2_1B_INSTRUCT,
    supports_reasoning: false,
    parameters: "1B",
    offline: false,
  },

  "tenacity-labs/magneta-chat": {
    family: "Magneta Chat",
    type: "Text to Text",
    maker: "Tenacity Labs",
    quantization: "bf-16",
    model_name: "Magneta Chat",
    response_format_json_object: false,
    structured_outputs: false,
    image_input: false,
    tools: false,
    context_size: 64_000,
    max_output_tokens: 4_096,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.TENACITY_LABS_MAGNETA_CHAT,
    offline: false,
    supports_reasoning: false,
    parameters: "70B",
  },

  "gravity/gravity-xs": {
    family: "Llama 3.2 1B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.2 1B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 8_192,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 3_000,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.GRAVITY_XS,
    supports_reasoning: false,
    parameters: "1B",
    offline: false,
  },

  "gravity/gravity-s": {
    family: "Llama 3.2 3B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.2 3B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 32_000,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 3_000,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.GRAVITY_S,
    supports_reasoning: false,
    parameters: "3B",
    offline: false,
  },

  "rizz/rizz-chat": {
    family: "Llama 3.1 70B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-16",
    model_name: "Meta Llama 3.1 70B Instruct FP16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 32_000,
    max_output_tokens: 2_500,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.RIZZ_CHAT,
    supports_reasoning: false,
    parameters: "70B",
    offline: false,
  },

  "cal-ai/food-vision-v4": {
    family: "CalAI Food Vision V3",
    type: "Text to Text",
    maker: "Google",
    quantization: "bf-16",
    model_name: "Google Gemma 3 27B Instruct BF16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 16_384,
    max_output_tokens: 3_000,
    is_model_supported: false,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.GEMMA_3_27B_INSTRUCT,
    supports_reasoning: false,
    parameters: "27B",
    offline: true,
  },

  "nvidia/nemotron3-super": {
    family: "Nemotron 3 Super",
    type: "Text to Text",
    maker: "NVIDIA",
    quantization: "FP8",
    model_name: "NVIDIA Nemotron 3 Super",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: true,
    context_size: 1_024_000,
    max_output_tokens: 500_000,
    is_model_supported: true,
    is_model_public: true,
    is_shown_on_playground: true,
    // Surfaced publicly as the "open weights" link on the model page, so this
    // must be the model's real repository rather than any id that resolves.
    hugging_face_identifier: HuggingFaceIdentifier.NVIDIA_NEMOTRON_3_SUPER_120B,
    supports_reasoning: false,
    // TODO
    parameters: "120B",
    offline: false,
  },

  "inference-net/schematron-v3": {
    family: "Schematron 3B",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "Schematron 3B",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 120_000,
    max_output_tokens: 8_192,
    is_model_supported: false,
    is_model_public: false,
    // TODO: update
    hugging_face_identifier: HuggingFaceIdentifier.INFERENCE_NET_SCHEMATRON_3B,
    supports_reasoning: false,
    parameters: "3B",
    offline: false,
  },

  "inference-net/schematron-v2-small": {
    family: "Schematron V2 Small",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "Schematron V2 Small",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 4_096,
    is_model_supported: true,
    is_model_public: true,
    hugging_face_identifier:
      HuggingFaceIdentifier.INFERENCE_NET_SCHEMATRON_V2_SMALL,
    supports_reasoning: false,
    parameters: "3B",
    offline: false,
  },

  "inference-net/schematron-v2-turbo": {
    family: "Schematron V2 Turbo",
    type: "Text to Text",
    maker: "InferenceNet",
    quantization: "bf-16",
    model_name: "Schematron V2 Turbo",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 128_000,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: true,
    hugging_face_identifier:
      HuggingFaceIdentifier.INFERENCE_NET_SCHEMATRON_V2_TURBO,
    supports_reasoning: false,
    parameters: "3B",
    offline: false,
  },

  "cal-ai/food-vision-v5": {
    family: "Qwen 2.5 7B Vision Instruct",
    type: "Text to Text",
    maker: "Qwen",
    quantization: "bf-16",
    model_name: "Qwen 2.5 7B Vision Instruct BF16",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: true,
    context_size: 32_000,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.QWEN_2_5_7B_INSTRUCT,
    supports_reasoning: false,
    parameters: "30B",
    offline: true,
  },

  "olive/analyze-product-v1": {
    family: "Olive Analyze Product V1",
    type: "Text to Text",
    maker: "Olive",
    quantization: "bf-16",
    model_name: "Olive Analyze Product V1",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: true,
    tools: false,
    context_size: 32_000,
    max_output_tokens: 8_192,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.OLIVE_ANALYZE_PRODUCT_V1,
    supports_reasoning: false,
    parameters: "9B",
    offline: true,
  },

  "gravity/gravity-v2": {
    family: "Llama 3.2 3B Instruct",
    type: "Text to Text",
    maker: "Meta",
    quantization: "fp-8",
    model_name: "Meta Llama 3.2 3B Instruct FP8",
    response_format_json_object: true,
    structured_outputs: true,
    image_input: false,
    tools: false,
    context_size: 8192,
    // Note: 128k is the actual model size but we say 16k for now to reduce GPU infra costs.
    max_output_tokens: 6144,
    is_model_supported: true,
    is_model_public: false,
    hugging_face_identifier: HuggingFaceIdentifier.GRAVITY_S,
    supports_reasoning: false,
    parameters: "3B",
    offline: false,
  },
} satisfies Record<InferenceModelType, InferenceModelMetadata>;
